import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  definirMotDePasse,
  verifierLienMotDePasse,
  type ApercuReinitialisation,
} from '../api/motdepasse';
import { useSession } from '../app/SessionContext';

/** Ce que l'écran sait afficher du lien. */
type Etat =
  | { readonly phase: 'chargement' }
  | { readonly phase: 'pret'; readonly apercu: ApercuReinitialisation }
  | { readonly phase: 'refus'; readonly message: string };

const CLASSE_CHAMP =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

/**
 * Page PUBLIQUE de réinitialisation : /mot-de-passe/:jeton.
 *
 * Aucune session requise — c'est justement l'accès perdu qu'on répare. Le lien est
 * à usage unique : dès que le mot de passe est enregistré, il est brûlé et toutes
 * les sessions du compte tombent.
 */
export function MotDePasse() {
  const { jeton = '' } = useParams();
  const naviguer = useNavigate();
  const { rafraichir } = useSession();
  const [etat, setEtat] = useState<Etat>({ phase: 'chargement' });
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string>();
  const [envoi, setEnvoi] = useState(false);
  const premier = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controleur = new AbortController();
    void verifierLienMotDePasse(jeton, { signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          setEtat({ phase: 'pret', apercu: resultat.donnees });
        } else {
          setEtat({
            phase: 'refus',
            message: resultat.message ?? 'Ce lien de réinitialisation n’est plus valable.',
          });
        }
      })
      .catch(() => {
        /* abandon : composant démonté */
      });
    return () => controleur.abort();
  }, [jeton]);

  useEffect(() => {
    if (etat.phase === 'pret') premier.current?.focus();
  }, [etat.phase]);

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();

    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setEnvoi(true);
    setErreur(undefined);
    const resultat = await definirMotDePasse(jeton, motDePasse);
    setEnvoi(false);

    if (resultat.ok) {
      await rafraichir();
      naviguer('/', { replace: true });
    } else {
      setErreur(resultat.message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--app-background)] p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-red-600" />
        <div className="p-8">
          <img
            src="/logo-registre.webp"
            alt="Registre-Sécurité — registre de sécurité incendie numérique"
            className="logo-marque mx-auto w-56 h-auto"
          />
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-red-600 text-center">
            Mot de passe
          </p>
          <h1 className="mt-1 text-lg font-semibold text-slate-900 text-center">
            Choisissez un nouveau mot de passe
          </h1>

          {etat.phase === 'chargement' && (
            <p className="mt-6 text-sm text-slate-500 text-center">Vérification du lien…</p>
          )}

          {etat.phase === 'refus' && (
            <div className="mt-6" role="alert">
              <p className="text-sm text-red-700 font-medium">Lien inutilisable</p>
              <p className="mt-1 text-sm text-slate-600">{etat.message}</p>
              <a href="/connexion" className="mt-4 inline-block text-sm text-red-700 underline">
                Aller à la connexion
              </a>
            </div>
          )}

          {etat.phase === 'pret' && (
            <form onSubmit={(evenement) => void soumettre(evenement)} className="mt-6 space-y-4">
              <dl className="rounded-lg bg-slate-50 border border-slate-100 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Compte</dt>
                  <dd className="text-slate-800 font-medium">{etat.apercu.emailMasque}</dd>
                </div>
                <div className="flex justify-between gap-4 mt-1">
                  <dt className="text-slate-500">Valable jusqu’au</dt>
                  <dd className="text-slate-800 mono">{etat.apercu.expireLe.slice(0, 16)}</dd>
                </div>
              </dl>

              <div>
                <label
                  htmlFor="nouveau-mot-de-passe"
                  className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1"
                >
                  Nouveau mot de passe
                </label>
                <input
                  id="nouveau-mot-de-passe"
                  ref={premier}
                  type="password"
                  value={motDePasse}
                  onChange={(evenement) => setMotDePasse(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="new-password"
                  minLength={10}
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  Dix caractères minimum. Une phrase fait très bien l’affaire.
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmation-mot-de-passe"
                  className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1"
                >
                  Confirmer
                </label>
                <input
                  id="confirmation-mot-de-passe"
                  type="password"
                  value={confirmation}
                  onChange={(evenement) => setConfirmation(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="new-password"
                  minLength={10}
                  required
                />
              </div>

              {erreur && (
                <p role="alert" className="text-sm text-red-700">
                  {erreur}
                </p>
              )}

              <button
                type="submit"
                disabled={envoi}
                className="w-full py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60"
              >
                {envoi ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Ce lien ne fonctionne qu’une fois, et toutes vos sessions ouvertes seront fermées.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
